/* Firm & Fair page events: the page half of TRACKING.md (the checkout half is checkout-pixel.js).
   Use: copy beside the page (landing/page-events.js) and load it once at the end of <body>:
   <script src="page-events.js" defer></script>, or paste it into the page's own script.
   It stores nothing and loads nothing. Every call goes through Funnel.track into the theme's consent-gated
   PostHog queue: only the published theme, a real visitor (not a ?funnel_internal=1 browser) and, in Europe or an
   unknown time zone, a yes in Shopify's banner let an event out. Elsewhere it prints to the console.
   The markup it reads (names and values: TRACKING.md "Page markup"):
     <section data-s="offer">                  section_view   (once per section, after 1 s at half in view)
     <article data-tier-card="team">           tier_view      (an edition's card seen for 1 s)
     <… data-tier-select="team">               tier_select    (picking an edition: a card tap or a radio, not a buy)
     <input type="checkbox" data-bump>         bump_toggle    (the Policy Pack box; data-tier="owner" if one per card)
     <a href="/cart/…" data-pos="offer">       buy_click      (edition, Policy Pack and value read from the link)
     <a href="#offer" data-cta="hero">         cta_click      (an in-page jump to the editions)
     <… data-sample="script_late_again">       sample_open    (a <details> when it opens, anything else on a tap)
     <details data-q="refund">                 faq_open
     <form data-f-signup="…">                  email_capture  (theme's funnel:signup, ok or known)
     <html data-angle="noshow">                angle on every event (the page sets it from the ad link's a=) */
(() => {
  'use strict';
  const d = document, F = window.Funnel;
  if (!F || !F.track || window.__ffPageEvents) return;
  window.__ffPageEvents = 1;
  const MAIN = 'firm-fair', BUMP = 'policy-pack';

  // the six properties every page event carries (also registered, so posthog-js's own events carry them)
  const ua = navigator.userAgent, C = F.config || {};
  const device = /iPad|Tablet|PlayBook|Silk|Android(?!.*Mobi)/i.test(ua) ? 'tablet'
    : /Mobi|iPhone|iPod|Android|IEMobile|BlackBerry|Opera Mini/i.test(ua) ? 'mobile' : 'desktop';
  const base = {
    page: C.page || 'firm-fair',
    page_variant: d.body.getAttribute('data-variant') || 'main',
    page_version: C.pv || 'none',
    in_app: F.inApp || 'none',
    device,
    // the ad's angle: its link carries a=<angle>; the page's first-paint script checks it and sets <html data-angle>
    angle: d.documentElement.getAttribute('data-angle') || 'none',
  };
  try { window.posthog && window.posthog.register(base); } catch (e) {}
  // the Meta ad this visit is credited to: the same click the theme writes onto the order (7-day last ad click)
  const adId = () => {
    let v = '';
    try { v = String((F.attributes() || {}).utm_content || new URLSearchParams(location.search).get('utm_content') || ''); } catch (e) {}
    return /^\d{6,30}$/.test(v) ? v : undefined;
  };
  const T = (ev, props, opt) => {
    try { const p = Object.assign({}, base, props), a = adId(); if (a) p.ad_id = a; F.track(ev, p, opt); } catch (e) {}
  };

  // landing_view: once, when the page is first on screen (background tabs and some crawlers never show it)
  const view = () => T('landing_view', {});
  if (d.visibilityState === 'visible') view();
  else d.addEventListener('visibilitychange', function shown() {
    if (d.visibilityState !== 'visible') return;
    d.removeEventListener('visibilitychange', shown); view();
  });

  // section_view and tier_view: once each, after 1 s at least half visible or covering half the screen
  const seen = new Set(), timers = new Map();
  const once = (key, send) => { if (seen.has(key)) return; seen.add(key); clearTimeout(timers.get(key)); timers.delete(key); send(); };
  const sectionView = (s, opt) => once('s:' + s, () => T('section_view', { section: s }, opt));
  const tierView = (t) => once('t:' + t, () => T('tier_view', { tier: t }));
  const keyOf = (el) => el.hasAttribute('data-s') ? 's:' + el.getAttribute('data-s') : 't:' + el.getAttribute('data-tier-card');
  const io = new IntersectionObserver((entries) => entries.forEach((e) => {
    const key = keyOf(e.target), vh = (e.rootBounds && e.rootBounds.height) || innerHeight;
    const half = e.isIntersecting && (e.intersectionRatio >= 0.5 || e.intersectionRect.height >= vh / 2);
    if (half) {
      if (!seen.has(key) && !timers.has(key)) timers.set(key, setTimeout(() => {
        key[0] === 's' ? sectionView(key.slice(2)) : tierView(key.slice(2));
      }, 1000));
    } else { clearTimeout(timers.get(key)); timers.delete(key); }
  }), { threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.75, 1] });
  d.querySelectorAll('[data-s],[data-tier-card]').forEach((el) => io.observe(el));

  // landing_scroll_depth: 25 / 50 / 75 / 100 % of the page height, once each
  const hit = new Set();
  addEventListener('scroll', () => {
    const h = d.documentElement.scrollHeight;
    if (!h) return;
    const pct = (scrollY + innerHeight + 2) / h * 100;
    [25, 50, 75, 100].forEach((m) => { if (pct >= m && !hit.has(m)) { hit.add(m); T('landing_scroll_depth', { depth: m }); } });
  }, { passive: true });

  // buy_click: read off the cart link itself, so the event says exactly what the checkout will hold
  const variants = () => {
    const m = new Map(), P = F.products || {};
    Object.keys(P).forEach((h) => { const p = P[h]; if (p) (p.variants || []).forEach((v) => m.set(String(v.id), { h, title: String(v.title || ''), price: v.price })); });
    return m;
  };
  const buy = (a) => {
    const href = a.getAttribute('href') || '', m = href.match(/\/cart\/(\d+:\d+(?:,\d+:\d+)*)/), V = variants();
    let cents = 0, lines = 0, bump = false, tier;
    (m ? m[1].split(',') : []).forEach((x) => {
      const [id, q] = x.split(':'), v = V.get(id), n = +q || 1;
      lines += 1;
      if (!v) return;
      cents += (+v.price || 0) * n;
      if (v.h === BUMP) bump = true;
      else if (v.h === MAIN) tier = /team/i.test(v.title) ? 'team' : /owner/i.test(v.title) ? 'owner' : undefined;
    });
    const sec = a.closest('[data-s]');
    // a buy tap means the editions were seen: if that second was not up yet, section_view offer goes first,
    // stamped a millisecond earlier, so every funnel keeps the order (both by beacon: in-app browsers close fast)
    sectionView('offer', { transport: 'sendBeacon', timestamp: new Date(Date.now() - 1) });
    T('buy_click', {
      tier: tier || a.getAttribute('data-tier') || 'unknown',
      bump,
      value: Math.round(cents) / 100,
      currency: 'USD',
      position: a.getAttribute('data-pos') || (sec && sec.getAttribute('data-s')) || 'offer',
      lines,
    }, { transport: 'sendBeacon' });
  };

  // taps: bubble phase, so the theme's capture-phase handler has already put the order attributes on cart links
  d.addEventListener('click', (e) => {
    const t = e.target && e.target.closest ? e.target : null;
    if (!t) return;
    const a = t.closest('a[href]');
    if (a && /\/cart\/\d+:\d+/.test(a.getAttribute('href') || '')) { buy(a); return; }
    const cta = t.closest('[data-cta]');
    if (cta) T('cta_click', { target: cta.getAttribute('data-cta') });
    const sel = t.closest('[data-tier-select]');
    if (sel && sel.tagName !== 'INPUT' && !t.closest('input,label')) T('tier_select', { tier: sel.getAttribute('data-tier-select') });
    const smp = t.closest('[data-sample]');
    if (smp && smp.tagName !== 'DETAILS') {
      const away = smp.tagName === 'A' && !/^#/.test(smp.getAttribute('href') || '#');
      T('sample_open', { asset: smp.getAttribute('data-sample') }, away ? { transport: 'sendBeacon' } : undefined);
    }
  });
  d.addEventListener('change', (e) => {
    const el = e.target;
    if (!el || el.tagName !== 'INPUT') return;
    if (el.hasAttribute('data-bump')) {
      const p = { on: !!el.checked };
      if (el.getAttribute('data-tier')) p.tier = el.getAttribute('data-tier');
      T('bump_toggle', p);
    }
    if (el.hasAttribute('data-tier-select') && el.checked) T('tier_select', { tier: el.getAttribute('data-tier-select') });
  });
  // <details> open: toggle does not bubble, so listen in the capture phase
  d.addEventListener('toggle', (e) => {
    const el = e.target;
    if (!el || el.tagName !== 'DETAILS' || !el.open) return;
    if (el.hasAttribute('data-q')) T('faq_open', { question: el.getAttribute('data-q') });
    if (el.hasAttribute('data-sample')) T('sample_open', { asset: el.getAttribute('data-sample') });
  }, true);
  // the theme's own signup forms (data-f-signup): only a real subscription counts
  d.addEventListener('funnel:signup', (e) => {
    const s = e.detail && e.detail.state, f = e.detail && e.detail.form;
    if (s === 'ok' || s === 'known') T('email_capture', { form: (f && f.getAttribute && f.getAttribute('data-f-signup')) || 'signup', state: s });
  });
})();
